'use client';

import { fetchGoodsList } from '@/api/product';
import { usePageCache } from '@/hooks/usePageCache';
import { useStore } from '@/providers/app-store-provider';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Box } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSparklyEyeglassesTracking } from '../hooks/useSparklyEyeglassesTracking';
import SparklyBanner from './SparklyBanner';
import SparklyMightLike from './SparklyMightLike';
import SparklyProductList from './SparklyProductList';
import SparklySeoInfo from './SparklySeoInfo';

// 类型定义
interface SparklyEyeglassesData {
  banner?: any;
  product?: { list: any[] };
  might_like?: { list: any[] };
  might_like_title?: string;
  group_info?: any;
  seo_data?: {
    title: string;
    keywords: string;
    description: string;
    og_image_url?: string;
    og_image_height?: string;
    og_image_width?: string;
  };
  bread_word?: string;
  group_limit?: number;
  group_id?: string;
  might_like_group_id?: string;
  seo_info?: any;
  ad_image?: any[];
  big_head_image?: Array<{
    shop_sku: string;
    image_url: string;
  }>;
  page_name?: string;
  current_path?: string;
}

interface Props {
  sparklyData: SparklyEyeglassesData;
  deviceType: EPlatform;
}

export default function SparklyEyeglassesContent({ sparklyData, deviceType }: Props) {
  // App相关状态
  const inAppH5 = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);
  const token = useStore((state) => state.token);
  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);
  const currency = useStore((state) => state.currency);

  // App用户信息加载状态
  const [appUserInfoLoaded, setAppUserInfoLoaded] = useState(false);

  // 客户端主商品数据状态
  const [clientProductData, setClientProductData] = useState<{
    productList: any[];
    loading: boolean;
    error: string | null;
  }>({
    productList: [],
    loading: false,
    error: null,
  });

  // ✅ 使用专门的埋点Hook
  const {
    trackThemePageView,
    trackThemePageLeave,
    trackThemePageProductExp,
    trackThemePageProductClick,
    trackThemePageADExp,
    trackThemePageADClick,
  } = useSparklyEyeglassesTracking();

  // ✅ 使用页面缓存 hook - 保存页面状态和滚动位置
  const { pageState } = usePageCache({
    pageKey: `sparkly-eyeglasses-page-${sparklyData.current_path || sparklyData.group_id || 'default'}`,
    initialData: {
      sparklyData,
      deviceType,
    },
  });

  // 避免重复API调用的标记（使用useRef避免状态更新）
  const hasCallClientAPIRef = useRef(false);

  // 客户端获取完整主商品数据
  const fetchClientProductData = useCallback(async () => {
    if (!sparklyData.group_id || !currency?.name || hasCallClientAPIRef.current) {
      return;
    }

    hasCallClientAPIRef.current = true; // 标记已经调用过
    setClientProductData((prev) => ({ ...prev, loading: true, error: null }));

    try {
      // 获取主商品列表（完整数据）
      const productResponse = await fetchGoodsList({
        currency: currency.name,
        group_id: parseInt(sparklyData.group_id, 10),
        limit: sparklyData.group_limit || 34,
        page: 1,
        is_sale: 1,
        in_stock: 1,
        append_correlation: 1,
      });

      const productList =
        productResponse.code === 200 ? (productResponse.data as any)?.list || [] : [];

      setClientProductData({
        productList,
        loading: false,
        error: null,
      });
    } catch {
      setClientProductData((prev) => ({
        ...prev,
        loading: false,
        error: '获取商品数据失败',
      }));
    }
  }, [sparklyData.group_id, sparklyData.group_limit, currency?.name]);

  // 组件挂载后获取客户端主商品数据（只调用一次）
  useEffect(() => {
    // 等待currency加载完成后再获取数据
    if (currency?.name && !hasCallClientAPIRef.current) {
      // 在App内嵌环境中，需要等待用户信息加载完成后再调用商品接口
      if (inAppH5) {
        // App环境：需要等待用户信息加载完成
        if (appUserInfoLoaded) {
          fetchClientProductData();
        }
      } else {
        // 非App环境：直接调用
        fetchClientProductData();
      }
    }
  }, [currency?.name, fetchClientProductData, inAppH5, appUserInfoLoaded]);

  // 在App内嵌环境时初始化通信
  useEffect(() => {
    if (inAppH5) {
      handleAppCommunication();
      window.addEventListener('appView', trackThemePageView);
      return () => {
        window.removeEventListener('appView', trackThemePageView);
      };
    }
  }, [inAppH5]);

  // 处理App通信逻辑
  const handleAppCommunication = async () => {
    try {
      // 清空当前用户信息
      setToken('');
      setUserInfo({});

      // 注册token回调通信
      appCommunicationManager.registerCallback(
        EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
        async (_token) => {
          logger.log('App登录token:', _token);
          if (_token !== 'ClearToken') {
            setToken(_token);
            // 获取用户信息
            await refreshAccountInfo();
            // 标记App用户信息已加载完成
            setAppUserInfoLoaded(true);
          } else {
            // 清除token时也要重置状态
            setAppUserInfoLoaded(false);
          }
        },
        {},
        'BackTokenInfo' as any, // 使用BackTokenInfo的postMessage方法
      );

      // 设置超时机制：如果5秒后仍未获取到用户信息，则标记为已加载完成（允许调用商品接口）
      setTimeout(() => {
        if (!appUserInfoLoaded) {
          logger.log('App用户信息获取超时，标记为已加载完成');
          setAppUserInfoLoaded(true);
        }
      }, 5000);
    } catch (error) {
      logger.error('App通信初始化失败:', error);
      // 如果通信初始化失败，也标记为已加载完成
      setAppUserInfoLoaded(true);
    }
  };
  // 组件销毁时清理App通信管理器
  useEffect(() => {
    return () => {
      if (inAppH5) {
        appCommunicationManager.cleanup();
      }
    };
  }, [inAppH5]);

  // 页面加载和卸载时的埋点处理
  useEffect(() => {
    // 页面加载时发送View事件
    trackThemePageView();
  }, [trackThemePageView]);

  // 参考首页的导航拦截处理 - 组件卸载时发送Leave事件
  useEffect(() => {
    return () => {
      trackThemePageLeave();
    };
  }, [trackThemePageLeave]);

  // 用户登出时清空App收藏数据和重置用户信息加载状态
  useEffect(() => {
    if (!token) {
      // 在App环境中，如果用户登出，重置用户信息加载状态
      if (inAppH5) {
        setAppUserInfoLoaded(false);
      }
    }
  }, [token, inAppH5]);

  // 智能选择主商品数据源：客户端数据（如果已加载）或服务端数据
  const getCurrentProductData = () => {
    // 如果客户端数据已加载完成且有数据，优先使用客户端数据
    if (!clientProductData.loading && clientProductData.productList.length > 0) {
      return {
        product: { list: clientProductData.productList },
      };
    }

    // 否则使用服务端数据
    return {
      product: { list: [] },
    };
  };

  const currentData = getCurrentProductData();
  return (
    <Box className="w-full min-h-screen">
      {/* Banner组件 */}
      {pageState.sparklyData.banner && (
        <SparklyBanner pageData={pageState.sparklyData.banner} deviceType={pageState.deviceType} />
      )}

      {/* 产品列表组件 */}
      {currentData.product && (
        <SparklyProductList
          pageData={{
            ...pageState.sparklyData,
            product: currentData.product,
          }}
          deviceType={pageState.deviceType}
          onProductExp={trackThemePageProductExp}
          onProductClick={trackThemePageProductClick}
          onAdExp={trackThemePageADExp}
          onAdClick={trackThemePageADClick}
        />
      )}

      {/* Might Like组件 - 由ActivityProductGrid自己调用API */}
      {pageState.sparklyData.might_like_group_id && (
        <SparklyMightLike
          might_like_title={pageState.sparklyData.might_like_title}
          might_like_group_id={pageState.sparklyData.might_like_group_id}
          deviceType={pageState.deviceType}
          onProductExp={trackThemePageProductExp}
          onProductClick={trackThemePageProductClick}
        />
      )}

      {/* SEO信息组件 */}
      {pageState.sparklyData.group_info &&
        (pageState.sparklyData.group_info.h_one ||
          pageState.sparklyData.group_info.pc_pure_text) && (
          <SparklySeoInfo
            groupInfo={pageState.sparklyData.group_info}
            deviceType={pageState.deviceType}
          />
        )}
    </Box>
  );
}
